export const THEME_STORAGE_KEY = 'rr-theme-preference';

/** Default theme inside the authenticated app when no Firestore preference exists. */
export const DEFAULT_APP_THEME: ThemePreference = 'light';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** Public marketing routes use light mode by default (hero may include intentional dark panels). */
export const MARKETING_ROUTES = ['/', '/how-it-works', '/for-emts', '/for-agencies', '/pricing'] as const;

export function isMarketingRoute(pathname: string): boolean {
  const path = pathname.replace(/\/$/, '') || '/';
  return (MARKETING_ROUTES as readonly string[]).includes(path);
}

export function getStoredThemePreference(): ThemePreference | null {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') {
      return value;
    }
  } catch {
    // localStorage unavailable (private browsing, etc.)
  }
  return null;
}

export function setStoredThemePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // ignore
  }
}

export function resolveTheme(preference: ThemePreference | null): ResolvedTheme {
  if (preference === 'light') return 'light';
  if (preference === 'dark') return 'dark';
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

export function applyResolvedTheme(resolved: ResolvedTheme): void {
  document.documentElement.setAttribute('data-theme', resolved);
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) {
    themeColor.setAttribute('content', resolved === 'dark' ? '#0d0f12' : '#0B4D3C');
  }
}

export function applyMarketingTheme(): void {
  document.documentElement.setAttribute('data-marketing', 'true');
  applyResolvedTheme('light');
}

export function clearMarketingTheme(): void {
  document.documentElement.removeAttribute('data-marketing');
}

export function applyThemePreference(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference);
  applyResolvedTheme(resolved);
  document.documentElement.setAttribute('data-theme-preference', preference);
  return resolved;
}

export function readProfileThemePreference(
  profile: { preferences?: { theme?: unknown } } | null | undefined,
): ThemePreference | null {
  const theme = profile?.preferences?.theme;
  if (theme === 'light' || theme === 'dark' || theme === 'system') {
    return theme;
  }
  return null;
}
